import { createPortal } from "preact/compat";
import { useEffect, useState } from "preact/hooks";
import { DotsButton } from "./ui";

/**
 * 행 끝의 `⋯` 메뉴.
 *
 * 메뉴를 `position: fixed` 로 띄우고 **클릭한 버튼의 화면 좌표**에 맞춘다. 셀 안에
 * absolute 로 두면 표를 감싼 `.scroll` 컨테이너(overflow-x:auto)가 잘라 버린다 — overflow
 * 는 한 축만 auto 로 둘 수 없어서 세로도 같이 클리핑되고, 마지막 행의 메뉴가 표 밖으로 못
 * 나온다.
 *
 * 좌표를 `ref` 가 아니라 **이벤트의 currentTarget** 에서 읽는다: Preact 는 함수 컴포넌트에
 * 달린 `ref` 를 props 로 넘기지 않아서(forwardRef 없이는 버려진다) `ref.current` 가 계속
 * null 이었고, 메뉴가 화면 좌상단에 붙었다. 눌린 버튼은 이벤트가 이미 들고 있으므로 ref
 * 자체가 필요 없다.
 *
 * 그리고 **body 로 포털한다.** `position: sticky` 는 z-index 와 무관하게 스택 컨텍스트를
 * 만들기 때문에, 고정된 작업 열(td) 안에서 렌더하면 메뉴가 그 셀의 컨텍스트에 갇혀 뒤
 * 행들의 sticky 셀에 덮인다 (z-index:21 을 줘도 소용없다 — 형제 컨텍스트끼리는 DOM 순서로
 * 겹친다). 실제로 그렇게 보여서 잡았다.
 *
 * 스타일을 JSX 객체로 주는 것도 의도다: CSP 가 `style-src 'self'` 라 마크업의 style **속성**
 * 은 차단된다. Preact 는 객체를 받으면 CSSOM(`el.style.top = ...`)으로 쓰는데 그건 CSP 대상이
 * 아니다. 여기 문자열 style 을 쓰면 조용히 위치가 안 잡힌다.
 */
export interface MenuItem {
  label: string;
  onClick: () => void;
  danger?: boolean;
}

export function RowMenu({ items }: { items: MenuItem[] }) {
  const [pos, setPos] = useState<{ top: number; right: number } | null>(null);

  useEffect(() => {
    if (!pos) return;
    const close = () => setPos(null);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setPos(null);
    };
    // 스크롤·리사이즈는 좌표를 낡게 만든다 — 따라 움직이게 만드는 대신 닫는다.
    // capture 로 받는 이유: 표 내부 스크롤은 window 까지 버블하지 않는다.
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
      window.removeEventListener("keydown", onKey);
    };
  }, [pos]);

  return (
    <>
      <DotsButton
        aria-expanded={pos !== null}
        onClick={(e) => {
          e.stopPropagation();
          if (pos) {
            setPos(null);
            return;
          }
          const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
          // 아래로 넘치면 위로 띄운다. 목록 마지막 행에서 메뉴가 화면 밖으로 나가는 걸 막는다.
          const height = items.length * 36 + 10;
          const below = window.innerHeight - r.bottom;
          setPos({
            top: below < height ? Math.max(8, r.top - height - 4) : r.bottom + 4,
            right: Math.max(8, window.innerWidth - r.right),
          });
        }}
      />
      {pos
        ? createPortal(
            <>
              <div class="menu-veil" onClick={() => setPos(null)} />
              <div
                class="menu"
                role="menu"
                style={{ top: `${pos.top}px`, right: `${pos.right}px` }}
              >
                {items.map((it) => (
                  <button
                    key={it.label}
                    role="menuitem"
                    class={it.danger ? "menu-item danger" : "menu-item"}
                    onClick={() => {
                      setPos(null);
                      it.onClick();
                    }}
                  >
                    {it.label}
                  </button>
                ))}
              </div>
            </>,
            document.body,
          )
        : null}
    </>
  );
}
